import { ROLE_LABEL } from '@kasboek/shared';
import { CaretUpDown, Check, Plus } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useLedgerSwitcher } from '../hooks/use-ledger-switcher';
import { NewLedgerDialog } from './new-ledger-dialog';

interface LedgerSwitcherProps {
  readonly placement?: 'below' | 'above';
}

export function LedgerSwitcher({ placement = 'below' }: LedgerSwitcherProps) {
  const { active, ledgers, isLoading, isSwitching, isOpen, containerRef, toggle, choose, close } = useLedgerSwitcher();
  const [creating, setCreating] = useState(false);

  if (isLoading || !active) {
    return <div className="h-14 w-full animate-pulse rounded-field bg-ink/5" aria-hidden="true" />;
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Tally aktif: ${active.name}. Ganti Tally`}
        className="flex min-h-14 w-full items-center gap-3 rounded-field border border-rule bg-card px-3 text-left hover:border-field"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{active.name}</span>
          <span className="block text-xs text-muted">
            {ROLE_LABEL[active.role]} · {active.memberCount} anggota
          </span>
        </span>
        <CaretUpDown size={18} className={isSwitching ? 'animate-pulse text-muted' : 'text-muted'} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.ul
            initial={{ opacity: 0, y: placement === 'below' ? -6 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: placement === 'below' ? -6 : 6 }}
            transition={{ duration: 0.16 }}
            className={`absolute z-30 w-full overflow-hidden rounded-card border border-rule bg-card py-1 shadow-lift ${
              placement === 'below' ? 'top-full mt-2' : 'bottom-full mb-2'
            }`}
          >
            {ledgers.map((ledger) => (
              <li key={ledger.id}>
                <button
                  type="button"
                  onClick={() => choose(ledger.id)}
                  aria-current={ledger.id === active.id ? 'true' : undefined}
                  className="flex min-h-11 w-full items-center gap-3 px-3 text-left hover:bg-paper"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{ledger.name}</span>
                    <span className="block text-xs text-muted">{ROLE_LABEL[ledger.role]}</span>
                  </span>
                  {ledger.id === active.id ? <Check size={16} weight="bold" className="text-ink" aria-hidden="true" /> : null}
                </button>
              </li>
            ))}
            <li className="mt-1 border-t border-rule pt-1">
              <button
                type="button"
                onClick={() => {
                  close();
                  setCreating(true);
                }}
                className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm text-ink hover:bg-paper"
              >
                <Plus size={16} weight="bold" aria-hidden="true" />
                Tally baru
              </button>
            </li>
          </motion.ul>
        ) : null}
      </AnimatePresence>

      <NewLedgerDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
